/**
 * Text Scroll Reveal Component
 * Adapted from Inspira UI Text Scroll Reveal
 * https://inspira-ui.com/docs/components/text-animations/text-scroll-reveal
 * 
 * Reveals text word by word as you scroll with sticky container
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export function initTextScrollReveal() {
  const containers = document.querySelectorAll('.text-scroll-reveal-container');
  
  containers.forEach((container) => {
    const text = container.dataset.text || '';
    if (!text) return;
    
    const sticky = container.querySelector('.text-scroll-reveal-sticky');
    const textElement = container.querySelector('.text-scroll-reveal-text');
    if (!sticky || !textElement) return;
    
    // Split text into words
    const words = text.split(/\s+/).filter(word => word.length > 0);
    
    // Clear and create word elements
    textElement.innerHTML = '';
    
    words.forEach((word, index) => {
      const wordWrapper = document.createElement('span');
      wordWrapper.className = 'scroll-word-wrapper';
      
      // Ghost text (background)
      const ghostText = document.createElement('span');
      ghostText.className = 'scroll-word-ghost';
      ghostText.textContent = word;
      
      // Main text (foreground)
      const mainText = document.createElement('span');
      mainText.className = 'scroll-word-main';
      mainText.textContent = word;
      mainText.style.opacity = '0';
      
      wordWrapper.appendChild(ghostText);
      wordWrapper.appendChild(mainText);
      textElement.appendChild(wordWrapper);
    });
    
    // Calculate scroll progress based on container position
    let scrollYProgress = 0;
    
    function updateScrollProgress() {
      const rect = container.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const containerHeight = container.offsetHeight;
      
      // Calculate progress more efficiently - animation happens in a shorter scroll distance
      // Start when container top reaches 80% of viewport, end when container bottom reaches 20% of viewport
      const startPoint = windowHeight * 0.8;
      const endPoint = -containerHeight * 0.2;
      const totalDistance = startPoint - endPoint;
      const currentPosition = rect.top;
      
      scrollYProgress = Math.max(0, Math.min(1, (startPoint - currentPosition) / totalDistance));
      
      // Update word opacities
      const wordWrappers = textElement.querySelectorAll('.scroll-word-wrapper');
      wordWrappers.forEach((wrapper, index) => {
        const mainText = wrapper.querySelector('.scroll-word-main');
        if (!mainText) return;
        
        const start = index / words.length;
        const end = (index + 1) / words.length;
        
        let opacity = 0;
        if (scrollYProgress < start) {
          opacity = 0;
        } else if (scrollYProgress > end) {
          opacity = 1;
        } else {
          // Linear interpolation
          opacity = (scrollYProgress - start) / (end - start);
        }
        
        gsap.to(mainText, {
          opacity: opacity,
          duration: 0.1,
          ease: 'none',
        });
      });
    }
    
    // Use ScrollTrigger for smooth updates
    ScrollTrigger.create({
      trigger: container,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 0.5,
      onUpdate: (self) => {
        const rect = container.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Calculate progress: (boundingRect.y / windowHeight) * -1
        // As we scroll DOWN: rect.top decreases, so scrollYProgress INCREASES
        // - When container enters from bottom: rect.top is large positive, scrollYProgress is large negative (~-1)
        // - As we scroll: rect.top decreases, scrollYProgress increases
        // - When container exits at top: rect.top is negative, scrollYProgress is positive (~1.2)
        scrollYProgress = (rect.top / window.innerHeight) * -1;
        
        // Normalize progress to 0-1 range for word reveal
        // Animation completes while content is still in view (container top at or above viewport top)
        const animationStart = -0.5;  // Start revealing when container is entering view (top ~50% down)
        const animationEnd = 0;       // Finish when container top reaches viewport top (content still visible)
        const normalizedProgress = Math.max(0, Math.min(1, (scrollYProgress - animationStart) / (animationEnd - animationStart)));
        
        // Update word opacities based on scroll progress
        const wordWrappers = textElement.querySelectorAll('.scroll-word-wrapper');
        wordWrappers.forEach((wrapper, index) => {
          const mainText = wrapper.querySelector('.scroll-word-main');
          if (!mainText) return;
          
          const start = index / words.length;
          const end = (index + 1) / words.length;
          
          let opacity = 0;
          if (normalizedProgress < start) {
            opacity = 0;
          } else if (normalizedProgress > end) {
            opacity = 1;
          } else {
            // Linear interpolation
            opacity = (normalizedProgress - start) / (end - start);
          }
          
          gsap.set(mainText, { opacity: opacity });
        });
      },
    });
    
    // Initial update
    updateScrollProgress();
  });
}

